import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { qrcodeQueryKeys } from '~/api/qrcode/queryKeys';
import { VCardMockup } from '~/components/atoms/mockup-previews/vcard-mockup/VCardMockup';
import { ConfirmNavigateModal } from '~/components/organisms/ConfirmNavigateModal';
import { DEFAULT_QR_CODE_STYLES } from '~/constants/common';
import { QRCODE_STATIC_STEP, QRCODE_STATIC_TYPE, QRCODE_STATUS } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import { useZodForm } from '~/hooks/useZodForm';
import { StaticQrContentLayout } from '~/pages/qrBuilder/p-static/components/static-qr-content-layout/StaticQrContentLayout';

import { StaticVCardForm } from './components/form/Form';
import { DEFAULT_VALUES, getSchema } from './utils/form-schema';
import { transformValuesToContent } from './utils/transform-values-to-content';

export const CreateStaticVCardContent = () => {
  const navigate = useNavigate();
  const form = useZodForm({ getSchema, defaultValues: DEFAULT_VALUES });
  const values = form.watch();

  const mutation = useMutation({
    mutationFn: qrcodeAPIService.createStaticQrVCard,
    mutationKey: [qrcodeQueryKeys.createStaticQrVCard],
  });

  const onSubmit = (values) => {
    if (mutation.isPending) return;

    mutation.mutate(
      {
        body: {
          name: values.name,
          style: DEFAULT_QR_CODE_STYLES,
          status: QRCODE_STATUS.DRAFT,
          content: transformValuesToContent(values),
          ...(values.qrCodeFolder ? { folder_id: values.qrCodeFolder } : {}),
          // isPasswordProtected: values.passwordProtection.isPasswordProtected,
          // password: values.passwordProtection.password,
        },
      },
      {
        onSuccess: (res) => {
          window.dataLayer?.push({
            event: 'create_qr_code',
            qr_code_id: res?.id,
            qr_code_type: `static-${QRCODE_STATIC_TYPE.VCARD.toLowerCase()}`,
          });
          navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.STYLES, QRCODE_STATIC_TYPE.VCARD, res.data.id));
        },
      },
    );
  };

  const handleClickStep = async (step) => {
    if (step === 0) navigate(PATH.QR.CREATE); // on back click
    if (step === 2) await form.handleSubmit(onSubmit)(); // on next click
  };

  return (
    <FormProvider {...form}>
      <StaticQrContentLayout
        handleStep={handleClickStep}
        isNextProcessing={mutation.isPending}
        activeStep={1}
        qrCode={{}}
        mockup={<VCardMockup values={values} />}
        form={<StaticVCardForm />}
        isDisabledQrCodePreview
        isMobileQrButtonHidden
      />
      <ConfirmNavigateModal activeStep={1} />
    </FormProvider>
  );
};
